import parseISO from "date-fns/parseISO"
import isValid from "date-fns/isValid"
import groupBy from "lodash/groupBy"

import defaultI18n from "~/i18n"

import lms from "~/images/libraries/lms_lms.svg"
import communication from "~/images/libraries/lms_communication.svg"
import community from "~/images/libraries/lms_community.svg"
import general from "~/images/libraries/general_library.svg"
import my_documents from "~/images/libraries/my_documents.svg"
import library_default from "~/images/libraries/library_default.svg"

import {
    buildQueryString,
    buildQueryFilters,
    parseQueryString,
    tryParseValue,
} from "~/lib/utils"
import { html2text } from "front-toolkit/lib/utils"

import QueryFiltersParser from "~/lib/QueryFiltersParser"

export function parents2filters(parents) {
    const filters = {}

    for (const parent of parents) {
        switch (parent.type) {
            case "area":
                filters["areaId"] = parent.id
                break

            case "library":
                filters["libraryId"] = parent.id
                break

            case "itinerary":
            case "CatalogItinerary":
                filters["itineraryId"] = parent.id
                filters["itinerary_name"] = "" // @hack: don't show itinerary_name field
                break

            case "school":
            case "CatalogSchool":
                filters["schoolId"] = parent.id
                break

            default:
                // TODO shouldn't rely on this default.
                filters[parent.type] = parent.id
        }
    }

    return filters
}

/*
 * "listId?parents=parent:id;parent:id" =>
 *
 * { listId, parents: [{ parent: id }, { parent: id }] }
 */
export function parseListKey(key) {
    const [listId, query = ""] = key.split("?")
    const values = parseQueryString(query)

    const parents = []
    if (values.parents) {
        for (const fv of values.parents.split(";")) {
            const [type, id] = fv.split(":")
            parents.push({ type, id })
        }
    }

    return { listId, parents }
}

/**
 * Returns:
 * {
 *      name: String,
 *      description: String,
 *      headerCoverUrl: String,
 *      headerStyle: Object,
 *      attributes: [{
 *          name: String,
 *          value: null,
 *      }]
 * }
 */
export function buildParentInfoForItem({
    item,
    totalCount,
    i18n = defaultI18n.global,
}) {
    switch (item._meta.type) {
        case "area":
            return buildParentInfoForArea(item, totalCount, i18n)

        case "library":
            return buildParentInfoForLibrary(item, totalCount, i18n)

        case "CatalogItinerary":
        case "itinerary":
            return buildParentInfoForItinerary(item, totalCount, i18n)

        case "CatalogSchool":
            return buildParentInfoForCatalogSchool(item, totalCount, i18n)

        case "school":
            return buildParentInfoForSchool(item, totalCount, i18n)

        default:
            throw new Error(
                `Don't know how to build info for ${item._meta.type}`
            )
    }
}

/**
 * Returns the info used by ListViewHeader, ModalBlock from a knowledge area.
 */
function buildParentInfoForArea(area, totalCount, i18n) {
    totalCount ??= area.programsCount
    return {
        name: area.name,
        description: html2text(area.description),
        headerCoverUrl: area.headerUrl,
        headerStyle: "",
        attributes: [
            {
                name: i18n.t("common.programs", totalCount),
                value: totalCount,
                icon: "program",
            },
        ],
    }
}

/**
 * Returns the info used by ListViewHeader, ModalBlock from a library
 */
function buildParentInfoForLibrary(library, totalCount, i18n) {
    totalCount ??= library.resourcesCount
    return {
        name: library.title,
        description: library.description,
        headerCoverUrl: library.headerUrl,
        headerStyle: getLibraryListHeaderStyle(library),
        attributes: [
            {
                name: i18n.t("common.resources", totalCount),
                value: totalCount,
                icon: "file",
            },
        ],
    }
}

/**
 * Returns the info used by ListViewHeader, ModalBlock for an itinerary
 */
function buildParentInfoForItinerary(itinerary, totalCount, i18n) {
    totalCount ??= itinerary.schools.length
    return {
        name: itinerary.name,
        description: itinerary.description ?? "",
        headerCoverUrl: itinerary.headerUrl,
        attributes: [
            {
                name: i18n.t("common.schools", totalCount),
                value: totalCount,
                icon: "program",
            },
            itinerary.level && {
                name: i18n.t("common.level"),
                value: itinerary.level,
            },
        ].filter(Boolean),
    }
}

/**
 * Returns the info used by ListViewHeader, ModalBlock from a catalog school
 */
function buildParentInfoForCatalogSchool(school, totalCount, i18n) {
    totalCount ??= school.programsCount
    return {
        name: school.name,
        description: school.description ?? "",
        headerCoverUrl: school.headerUrl,
        attributes: [
            {
                name: i18n.t("common.programs", totalCount),
                value: totalCount,
                icon: "program",
            },
        ],
    }
}

/**
 * Returns the info used by ListViewHeader, ModalBlock from a school
 */
function buildParentInfoForSchool(school, totalCount, i18n) {
    totalCount ??= school.callsCount
    return {
        name: school.name,
        description: school.description ?? "",
        headerCoverUrl: school.headerUrl ?? school.coverUrl, // TODO
        attributes: [
            {
                name: i18n.t("common.calls", totalCount),
                value: totalCount,
                icon: "program",
            },
        ],
    }
}

function getLibraryListHeaderStyle(library) {
    const libraryCode = library.libraryCode

    const primaryLibraryBackgrounds = {
        lms: {
            backgroundImage: lms,
            backgroundPosition: "right 62px bottom 10px",
        },
        communication: {
            backgroundImage: communication,
            backgroundPosition: "right 62px bottom 0px",
        },
        community: {
            backgroundImage: community,
            backgroundPosition: "right 62px bottom 10px",
        },
        general_library: {
            backgroundImage: general,
            backgroundPosition: "right 62px top 32px",
        },
        my_documents: {
            backgroundImage: my_documents,
            backgroundPosition: "right 62px top 0px",
        },
    }

    const defaultBackground = {
        backgroundImage: library_default,
        backgroundPosition: "right 63px top -20px",
    }

    const isPrimaryLibrary = libraryCode in primaryLibraryBackgrounds

    const backgroundImage = isPrimaryLibrary
        ? primaryLibraryBackgrounds[libraryCode]["backgroundImage"]
        : defaultBackground["backgroundImage"]

    const backgroundPosition = isPrimaryLibrary
        ? primaryLibraryBackgrounds[libraryCode]["backgroundPosition"]
        : defaultBackground["backgroundPosition"]
    return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition,
    }
}

// See lib/QueryFiltersParser.js for doc.
export function parseQueryFilters(str) {
    const parser = new QueryFiltersParser(str)
    return parser.parse()
}

function isValidISO8601(str) {
    return isValid(parseISO(str))
}

function isValidNumber(str) {
    return !Number.isNaN(Number(str))
}

// `parseQueryFilters` is a general purpose function that only deals
// with understanding the filter string syntax.
//
// This function goes beyond that:
// * It tries to parse number and boolean values.
// * Merges array values.
// * It deals with repeated filters: filters that represent a range ('range' for numbers, 'date' for
//   dates, 'size' for file sizes) are send as two separate filters with the same name and operators
//   'GE' and 'LE'. For example: "foo:1;GE|foo:10;LE" represents a 'range' filter of name "foo".
//   When two filters with the same name are found, we check that the operators are 'GE' and 'LE',
//   and we test that the values are compatible with a 'range', 'date' or 'size' filter.
//   If they are, the filters are merged. Otherwise, an error is thrown.
// * It returns values and operators in a format that can be directly used in listStore.
export function cookQueryFilters(allFilters) {
    const values = {}
    const operators = {}

    for (const filter of allFilters) {
        filter.value = decodeURIComponent(filter.value)
    }

    for (const [name, filters] of Object.entries(groupBy(allFilters, "name"))) {
        if (filters.every(f => f.array)) {
            values[name] = filters.map(f => f.value).map(tryParseValue)
            operators[name] = filters.map(f => f.operator)
        } else {
            if (filters.some(f => f.array))
                throw new Error("Mixing array and non-array")

            if (filters.length === 1) {
                const { name, value, operator } = filters[0]
                values[name] = tryParseValue(value)
                operators[name] = operator
            } else if (filters.length === 2) {
                let [start, end] = filters
                if (start.operator === "LE") [start, end] = [end, start]

                if (start.operator !== "GE" && end.operator !== "LE")
                    throw new Error("Invalid operators")
                if (isValidISO8601(start.value) && isValidISO8601(end.value)) {
                    values[name] = {
                        start: start.value,
                        end: end.value,
                    }
                } else if (
                    isValidNumber(start.value) &&
                    isValidNumber(end.value)
                ) {
                    values[name] = {
                        start: Number(start.value),
                        end: Number(end.value),
                    }
                } else {
                    throw new Error("Invalid filter values")
                }
            } else {
                throw new Error("More than two filters with the same name")
            }
        }
    }

    return { values, operators }
}

export function buildListQueryString({
    description,
    instance,
    parents,
    more = false,
}) {
    const parentFilters = parents2filters(parents)
    const { filters, operators, orderBy, direction, includeTotalCount } =
        instance

    return buildQueryString({
        order: orderBy,
        orderType: direction,
        filter: buildQueryFilters(
            {
                ...parentFilters,
                ...filters,
            },
            description.filters,
            operators
        ),
        ...(more ? { cursor: instance.nextCursor } : {}),
        totalCount: includeTotalCount,
    })
}
